import { defineStore } from 'pinia'
import { ref, computed } from 'vue'
import { useAuthStore } from './auth'

export const useMerchantStore = defineStore('merchant', () => {
  // State
  const currentMerchant = ref(null)
  const merchants = ref([])
  const merchantRole = ref(null)
  const dashboardStats = ref(null)
  const isLoading = ref(false)
  const error = ref(null)
  const isInitialized = ref(false)

  // Getters
  const hasMerchantAccess = computed(() => merchants.value.length > 0)
  const merchantName = computed(() => currentMerchant.value?.display_name || '')
  const merchantId = computed(() => currentMerchant.value?.id || null)

  // Actions
  async function initializeMerchantContext() {
    if (isInitialized.value) return true

    const authStore = useAuthStore()
    if (!authStore.isAuthenticated) return false

    try {
      isLoading.value = true
      error.value = null

      const response = await authStore.rpcClient.call('hub.dashboard@getContext')

      currentMerchant.value = response.merchant
      merchants.value = response.merchants || []
      merchantRole.value = response.role

      // Also fetch dashboard stats
      if (response.merchant) {
        await fetchDashboardStats()
      }

      isInitialized.value = true
      return true
    } catch (err) {
      error.value = err.message || 'Failed to load merchant context'
      console.error('Failed to initialize merchant context:', err)
      return false
    } finally {
      isLoading.value = false
    }
  }

  async function switchMerchant(merchantId) {
    const authStore = useAuthStore()

    try {
      isLoading.value = true
      error.value = null

      const response = await authStore.rpcClient.call('hub.dashboard@switchMerchant', {
        merchant_id: merchantId,
      })

      currentMerchant.value = response.merchant
      merchantRole.value = response.role

      // Refresh dashboard stats for new merchant
      await fetchDashboardStats()

      return true
    } catch (err) {
      error.value = err.message || 'Failed to switch merchant'
      console.error('Failed to switch merchant:', err)
      return false
    } finally {
      isLoading.value = false
    }
  }

  async function fetchDashboardStats(days = 30) {
    const authStore = useAuthStore()

    const response = await authStore.rpcClient.call('hub.dashboard@getStats', {
      merchant_id: currentMerchant.value?.id,
      days,
    })

    dashboardStats.value = response
    return response
  }

  function reset() {
    currentMerchant.value = null
    merchants.value = []
    merchantRole.value = null
    dashboardStats.value = null
    isLoading.value = false
    error.value = null
    isInitialized.value = false
  }

  return {
    // State
    currentMerchant,
    merchants,
    merchantRole,
    dashboardStats,
    isLoading,
    error,
    isInitialized,

    // Getters
    hasMerchantAccess,
    merchantName,
    merchantId,

    // Actions
    initializeMerchantContext,
    switchMerchant,
    fetchDashboardStats,
    reset,
  }
})
